import IdeList from '@github-ui/brand-core/components/IdeList'
import CopilotIconAnimation from '@github-ui/copilot-chat/components/CopilotIconAnimation'
import {LegalDisclaimer} from '@github-ui/copilot-chat/components/LegalDisclaimer'
import ProductAnnouncement from '@github-ui/product-announcement/components/ProductAnnouncement'
import type {
  ProductAnnouncementBannerEvent,
  ProductAnnouncementModalEvent,
} from '@github-ui/product-announcement/components/ProductAnnouncement.types'
import {Heading, Text} from '@primer/react'
import {useEffect, useState} from 'react'

import {useCopilotContext} from './../../contexts/CopilotContext'
import {ChatInput, type ChatInputProps} from './../ChatInput'
import {SignInDialog} from './../SignInDialog'
import {COPY, getProductAnnouncementId} from './ChatContentV2.data'
import Styles from './ChatContentV2.module.css'

export interface ChatContentV2Props {
  initialModelId?: ChatInputProps['currentModelId']
  onIdeListItemClick: (ideId: string, ideName: string) => void
  onProductAnnouncementBannerEvent: (event: ProductAnnouncementBannerEvent, data?: Record<string, unknown>) => void
  onProductAnnouncementModalEvent: (event: ProductAnnouncementModalEvent, data?: Record<string, unknown>) => void
}

const defaultProps: Partial<ChatContentV2Props> = {}

export const ChatContentV2 = (props: ChatContentV2Props) => {
  const initializedProps: ChatContentV2Props = {...defaultProps, ...props}
  const {initialModelId, onIdeListItemClick, onProductAnnouncementBannerEvent, onProductAnnouncementModalEvent} =
    initializedProps

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [currentModelId, setCurrentModelId] = useState(initialModelId || null)
  const {prompt, setPrompt} = useCopilotContext()

  const productAnnouncementId = getProductAnnouncementId()

  useEffect(() => {
    if (initialModelId && !currentModelId) setCurrentModelId(initialModelId || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialModelId])

  return (
    <div className={Styles['ChatContentV2']}>
      <div className={Styles['ChatContentV2__contentWrapper']}>
        <div className={Styles['ChatContentV2__content']}>
          <div className={Styles['ChatContentV2__header']}>
            <CopilotIconAnimation hidden />

            <div className={Styles['ChatContentV2__heading']}>
              <Heading as="h1" className={Styles['ChatContentV2__heading-title']}>
                {COPY.heading.title}
              </Heading>
              <Text as="p" size="large" className={Styles['ChatContentV2__heading-description']}>
                {COPY.heading.description}
              </Text>
            </div>
          </div>

          <div className={Styles['ChatContentV2__body']}>
            <div className={Styles['ChatContentV2__input']}>
              <ChatInput
                currentModelId={currentModelId}
                prompt={prompt}
                onPromptChange={setPrompt}
                onOpenDialog={() => setIsDialogOpen(true)}
                onModelClick={setCurrentModelId}
              />
            </div>

            {productAnnouncementId && (
              <div className={Styles['ChatContentV2__productAnnouncement']}>
                <ProductAnnouncement
                  id="copilot-immersive-logged-out"
                  announcementId={productAnnouncementId}
                  shouldShowTabCtas={false}
                  shouldShowMainCta
                  useLocalStorage
                  onBannerEvent={onProductAnnouncementBannerEvent}
                  onModalEvent={onProductAnnouncementModalEvent}
                />
              </div>
            )}
          </div>
        </div>

        <div className={Styles['ChatContentV2__ideList']}>
          <Text as="div" size="medium" className={Styles['ChatContentV2__ideList-title']}>
            {COPY.ideList.title}
          </Text>

          <IdeList onItemClick={onIdeListItemClick} excludeList={['github']} size="small" />
        </div>
      </div>

      <div className={Styles['ChatContentV2__footer']}>
        <LegalDisclaimer />
      </div>

      {/* TODO: Implement flow for generated Copilot response using selected model */}
      {isDialogOpen && <SignInDialog onClose={() => setIsDialogOpen(false)} />}
    </div>
  )
}
